import styled from "@emotion/styled"
import { PHeading, PTag, PText } from "@porsche-design-system/components-react"
import {
	spacingFluidMd,
	spacingFluidXl,
	spacingStaticLg,
	spacingStaticSm,
	spacingStaticXl,
} from "@porsche-design-system/components-react/emotion"
import type { PricingView } from "@slfinrtl/ui-bff-routes"
import { FormattedMessage, defineMessages, useIntl } from "react-intl"
import { emptyPlaceholder } from "src/common/emptyPlaceholder"
import { commonMessages } from "src/common/messages/common.messages"
import { Grid, GridItem, List, Msrp, PriceBreakdown, PriceHistory, priceBreakdownMessages } from "src/components"
import { ExtraCostsPriceBreakdown } from "./ExtraCostsPriceBreakdown"

const messages = defineMessages({
	customerPrice: {
		defaultMessage: "Customer Price",
		id: "vehicleDetails.overview.listing.customerPrice",
	},
	minimalPrice: {
		defaultMessage: "Minimal Price",
		id: "vehicleDetails.overview.listing.minimalPrice",
	},
	onlineOrderingLabel: {
		defaultMessage: "Online Orderable",
		id: "vehicleDetails.overview.listing.onlineOrdering",
	},
	priceNet: {
		defaultMessage: "Price without taxes",
		id: "vehicleDetails.overview.listing.priceNet",
	},
	purchasePrice: {
		defaultMessage: "Purchase Price",
		id: "vehicleDetails.overview.listing.purchasePrice",
	},
	vatDeductible: {
		defaultMessage: "Vat deductible",
		id: "vehicleDetails.overview.listing.vatDeductible",
	},
	vehiclePrice: {
		defaultMessage: "Vehicle Price",
		id: "vehicleDetails.overview.listing.vehiclePrice",
	},
	visibility: {
		defaultMessage: "Visibility",
		id: "vehicleDetails.overview.listing.visibility",
	},
})

interface PricingProps {
	isVatDeductibleAvailable: boolean
	usePriceHistory: boolean
	view: PricingView
}

export const Pricing = (props: PricingProps) => {
	const { formatMessage } = useIntl()
	const { isVatDeductibleAvailable, usePriceHistory, view } = props
	const {
		australiaNetPrice,
		customerPrice,
		formattedMsrp,
		minimumPrice,
		priceBreakdown,
		priceComponents,
		priceHistory,
		pricingUseCase,
		purchasePrice,
		vatDeductible,
	} = view

	return (
		<Grid $columnGap={{ base: spacingFluidMd, s: spacingFluidXl }} $rowGap={spacingStaticLg}>
			<StyledGridItem size={{ base: 12, m: 6 }}>
				<List>
					{pricingUseCase === "EXTRA_COSTS_PRICE_BREAKDOWN" ? (
						<List.Item
							label={formatMessage(messages.vehiclePrice)}
							value={
								priceBreakdown
									? priceBreakdown.categories[0]?.totalPrice
									: (customerPrice.value ?? emptyPlaceholder)
							}
						/>
					) : (
						<List.Item
							label={formatMessage(messages.customerPrice)}
							value={
								<Center>
									<PText weight="bold">{customerPrice.value ?? emptyPlaceholder}</PText>
									{customerPrice.vatDeductible && isVatDeductibleAvailable && (
										<StyledTag>
											<FormattedMessage {...commonMessages.vat} />
										</StyledTag>
									)}
								</Center>
							}
						/>
					)}
					{isVatDeductibleAvailable && (
						<List.Item label={formatMessage(messages.vatDeductible)} value={vatDeductible} />
					)}
					{priceComponents?.items.map((item, index) => (
						<List.Item key={`item_${index}`} label={item.name} value={item.price} />
					))}
					<List.Item label={formatMessage(messages.minimalPrice)} value={minimumPrice} />
					<List.Item label={formatMessage(messages.purchasePrice)} value={purchasePrice} />
					{australiaNetPrice && (
						<List.Item label={formatMessage(messages.priceNet)} value={australiaNetPrice} />
					)}
				</List>

				{pricingUseCase === "EXTRA_COSTS_PRICE_BREAKDOWN" && <ExtraCostsPriceBreakdown view={view} />}
			</StyledGridItem>

			<StyledGridItemPriceEstimationAndHistory size={{ base: 12, m: 6 }}>
				{priceHistory.length > 0 && usePriceHistory && <PriceHistory view={view.priceHistory} />}
				{formattedMsrp && <Msrp view={formattedMsrp} />}
				{priceBreakdown && pricingUseCase !== "EXTRA_COSTS_PRICE_BREAKDOWN" && (
					<div>
						<PHeading size="md">
							<FormattedMessage {...priceBreakdownMessages.title} />
						</PHeading>
						<PriceBreakdownWrapper>
							<PriceBreakdown view={priceBreakdown} collapsible />
						</PriceBreakdownWrapper>
					</div>
				)}
			</StyledGridItemPriceEstimationAndHistory>
		</Grid>
	)
}

const StyledGridItem = styled(GridItem)`
	display: flex;
	flex-direction: column;
	gap: ${spacingStaticLg};
`

const StyledGridItemPriceEstimationAndHistory = styled(StyledGridItem)`
	gap: ${spacingStaticXl};
`

const PriceBreakdownWrapper = styled.div`
	margin-top: 24px;
`

const StyledTag = styled(PTag)`
	margin: -4px 0 -4px ${spacingStaticSm};
`

const Center = styled.div`
	display: flex;
	align-items: center;
`
